import { COUNTRY } from '~globals';
import { graphql, useStaticQuery } from 'gatsby';
import { useMemo } from 'react';
import { createPathArray, createPathFromPathArray } from '~build/utils';
import { keyBy } from '~utils/array';

import type { ContentfulPageContainerNode } from '~build/models';
import type { Country } from '~models/country';

export const query = graphql`
  {
    allContentfulPageContainer {
      nodes {
        id
        name
        country
        slug
        parentPage {
          id
        }
        metaFields {
          metaTitle
        }
      }
    }
  }
`;

type ContentfulPageContainerNodeWithCountry = ContentfulPageContainerNode & {
  country: string;
};

type ContentfulPageNodes = {
  allContentfulPageContainer: {
    nodes: ContentfulPageContainerNodeWithCountry[];
  };
};

export type LandingPagePaths = Record<string, { id: string; path: string }>;

const createContentfulLandingPagePaths = (nodes: ContentfulPageContainerNodeWithCountry[]): LandingPagePaths => {
  const countries: Record<Country, ContentfulPageContainerNodeWithCountry[]> = {
    nl: nodes.filter(({ country }) => country === 'nl'),
    de: nodes.filter(({ country }) => country === 'de'),
    fr: nodes.filter(({ country }) => country === 'fr'),
  };

  const filteredNodes = countries[COUNTRY] || [];

  const hash = keyBy(filteredNodes, 'id');

  const pages = filteredNodes.map((node) => ({
    id: node.id,
    path: createPathFromPathArray(createPathArray(node, hash)),
  }));

  return keyBy(pages, 'id');
};

export const useLandingPagePaths = (): LandingPagePaths => {
  const { allContentfulPageContainer } = useStaticQuery<ContentfulPageNodes>(query);
  return useMemo(() => createContentfulLandingPagePaths(allContentfulPageContainer.nodes), []);
};
